import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    AnimatePresence,
    motion,
    useMotionValue,
    useReducedMotion,
    useSpring,
} from "framer-motion";
import "./emmbot.css";

const MESSAGES = [
    "Hola 👋 Soy Bot Empi, el bot de EMMPIEZA",
    "¿Tienes una idea con IA? Hablemos",
    "Hacemos pentesting y seguridad web",
    "Haz clic y cuéntanos tu proyecto 🚀",
];

const CRASH_MESSAGES = [
    "¡Ups! 💥 Eso no estaba en mi entrenamiento",
    "404: frenos no encontrados 🙈",
    "¿Quién puso esa pared ahí? 🤕",
    "Tranquilos, soy de metal 🤖",
];

// Puntos de descanso (fracciones del viewport), pegados a los bordes
// para no tapar el contenido.
const SPOTS = [
    { x: 0.86, y: 0.24 },
    { x: 0.87, y: 0.52 },
    { x: 0.85, y: 0.78 },
    { x: 0.1, y: 0.3 },
    { x: 0.09, y: 0.58 },
    { x: 0.11, y: 0.8 },
];

const REST_MIN_MS = 6000; // tiempo mínimo quieto en cada punto
const REST_EXTRA_MS = 4000; // + aleatorio
const TRAVEL_DURATION_S = 3.4; // viaje normal, suave
const CRASH_TRAVEL_S = 1.2; // cuando "descacha" sale acelerado
const CRASH_CHANCE = 0.25;
const CRASH_PAUSE_MS = 3200; // aturdido contra la pared
const BUBBLE_VISIBLE_MS = 5000;
const BUBBLE_INTERVAL_MS = 16000;
const FIRST_BUBBLE_DELAY_MS = 3000;

function randomSpot(exclude) {
    let next = exclude;
    while (next === exclude) {
        next = Math.floor(Math.random() * SPOTS.length);
    }
    return next;
}

function EmmBot({ onEngage }) {
    const reduceMotion = useReducedMotion();
    const hoveringRef = useRef(false);
    const timerRef = useRef(null);
    const bounceTimerRef = useRef(null);

    // Fases: resting | traveling | crash-traveling | crashed
    const [phase, setPhase] = useState("resting");
    const [, setSpotIndex] = useState(0);
    const [target, setTarget] = useState(SPOTS[0]);
    const [tilt, setTilt] = useState(0);
    const [bubble, setBubble] = useState(null);
    const crashSideRef = useRef(1);

    const isMoving = phase === "traveling" || phase === "crash-traveling";

    const scheduleNextMove = useCallback(() => {
        if (reduceMotion) return;
        clearTimeout(timerRef.current);
        const wait = REST_MIN_MS + Math.random() * REST_EXTRA_MS;
        timerRef.current = setTimeout(() => {
            // Si el usuario tiene el cursor encima, espera otro turno
            if (hoveringRef.current) {
                scheduleNextMove();
                return;
            }
            setSpotIndex((current) => {
                const cur = SPOTS[current];
                if (Math.random() < CRASH_CHANCE) {
                    // Descache: sale disparado contra la pared más cercana.
                    // El punto queda DENTRO de la pantalla para que el
                    // impacto se vea completo.
                    const side = cur.x > 0.5 ? 1 : -1;
                    crashSideRef.current = side;
                    setTarget({ x: side > 0 ? 0.955 : 0.045, y: cur.y });
                    setTilt(side * 14);
                    setPhase("crash-traveling");
                    return current;
                }
                const next = randomSpot(current);
                setTarget(SPOTS[next]);
                setTilt(SPOTS[next].x > cur.x ? 7 : -7);
                setPhase("traveling");
                return next;
            });
        }, wait);
    }, [reduceMotion]);

    useEffect(() => {
        scheduleNextMove();
        return () => {
            clearTimeout(timerRef.current);
            clearTimeout(bounceTimerRef.current);
        };
    }, [scheduleNextMove]);

    const handleArrival = () => {
        if (phase === "traveling") {
            setPhase("resting");
            setTilt(0);
            scheduleNextMove();
        } else if (phase === "crash-traveling") {
            setPhase("crashed");
            setTilt(0);
            setBubble(
                CRASH_MESSAGES[Math.floor(Math.random() * CRASH_MESSAGES.length)],
            );
            // Rebote: tras aplastarse contra la pared, sale despedido
            // un poco hacia atrás mientras sigue mareado.
            clearTimeout(bounceTimerRef.current);
            bounceTimerRef.current = setTimeout(() => {
                const side = crashSideRef.current;
                setTarget((prev) => ({
                    x: side > 0 ? 0.87 : 0.13,
                    y: prev.y,
                }));
            }, 550);
            clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => {
                setBubble(null);
                setSpotIndex((current) => {
                    const next = randomSpot(current);
                    setTarget(SPOTS[next]);
                    setTilt(SPOTS[next].x > SPOTS[current].x ? 7 : -7);
                    setPhase("traveling");
                    return next;
                });
            }, CRASH_PAUSE_MS);
        }
    };

    // Las pupilas siguen el cursor
    const pupilX = useSpring(useMotionValue(0), { stiffness: 120, damping: 14 });
    const pupilY = useSpring(useMotionValue(0), { stiffness: 120, damping: 14 });

    useEffect(() => {
        if (reduceMotion) return undefined;
        const onMove = (e) => {
            pupilX.set((e.clientX / window.innerWidth - 0.5) * 5);
            pupilY.set((e.clientY / window.innerHeight - 0.5) * 4);
        };
        window.addEventListener("mousemove", onMove);
        return () => window.removeEventListener("mousemove", onMove);
    }, [reduceMotion, pupilX, pupilY]);

    // Mensajes normales, con calma; nunca pisan el mensaje de choque
    useEffect(() => {
        let idx = 0;
        let hideTimer;
        const show = () => {
            setBubble((prev) => {
                if (prev) return prev; // ya hay algo en pantalla (p. ej. choque)
                hideTimer = setTimeout(() => setBubble(null), BUBBLE_VISIBLE_MS);
                const msg = MESSAGES[idx % MESSAGES.length];
                idx += 1;
                return msg;
            });
        };
        const firstTimer = setTimeout(show, FIRST_BUBBLE_DELAY_MS);
        const loop = setInterval(show, BUBBLE_INTERVAL_MS);
        return () => {
            clearTimeout(firstTimer);
            clearInterval(loop);
            clearTimeout(hideTimer);
        };
    }, []);

    const goToContact = () => {
        if (onEngage) {
            onEngage();
            return;
        }
        document.getElementById("contacto")?.scrollIntoView({
            behavior: reduceMotion ? "auto" : "smooth",
            block: "start",
        });
    };

    const bubbleSide =
        target.x > 0.5 ? "emmbot-bubble--left" : "emmbot-bubble--right";

    return (
        <motion.div
            className="emmbot"
            initial={false}
            animate={{ left: `${target.x * 100}%`, top: `${target.y * 100}%` }}
            transition={
                reduceMotion
                    ? { duration: 0 }
                    : phase === "crash-traveling"
                        ? { duration: CRASH_TRAVEL_S, ease: [0.5, 0, 1, 0.6] }
                        : phase === "crashed"
                            ? { type: "spring", stiffness: 160, damping: 11 }
                            : { duration: TRAVEL_DURATION_S, ease: [0.45, 0.05, 0.35, 1] }
            }
            onAnimationComplete={handleArrival}
        >
            <AnimatePresence>
                {bubble && (
                    <motion.div
                        className={`emmbot-bubble ${bubbleSide}`}
                        role="status"
                        initial={{ opacity: 0, y: 6, scale: 0.92 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 4, scale: 0.95 }}
                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                    >
                        {bubble}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Impacto contra la pared: explosión, chispas y mareo */}
            <AnimatePresence>
                {phase === "crashed" && !reduceMotion && (
                    <motion.div
                        className={`emmbot-impact ${crashSideRef.current > 0
                                ? "emmbot-impact--right"
                                : "emmbot-impact--left"
                            }`}
                        initial={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        aria-hidden="true"
                    >
                        <motion.span
                            className="emmbot-impact-boom"
                            initial={{ scale: 0, opacity: 1 }}
                            animate={{ scale: [0, 1.5, 1.1], opacity: [1, 1, 0] }}
                            transition={{ duration: 0.75, ease: "easeOut" }}
                        >
                            💥
                        </motion.span>
                        {[0, 1, 2, 3, 4].map((i) => (
                            <motion.span
                                key={i}
                                className="emmbot-impact-spark"
                                initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                                animate={{
                                    x:
                                        (crashSideRef.current > 0 ? -1 : 1) *
                                        (16 + i * 8) *
                                        (i % 2 ? 0.65 : 1),
                                    y: (i - 2) * 15,
                                    opacity: 0,
                                    scale: 0.25,
                                }}
                                transition={{ duration: 0.55 + i * 0.06, ease: "easeOut" }}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            {phase === "crashed" && !reduceMotion && (
                <motion.div
                    className="emmbot-dizzy"
                    aria-hidden="true"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.1, repeat: Infinity, ease: "linear" }}
                >
                    <span>✦</span>
                    <span>✦</span>
                    <span>✦</span>
                </motion.div>
            )}

            {/* Humo de cohete mientras viaja */}
            <AnimatePresence>
                {isMoving && !reduceMotion && (
                    <motion.div
                        className="emmbot-smoke"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        {[0, 1, 2].map((i) => (
                            <motion.span
                                key={i}
                                className="emmbot-smoke-puff"
                                animate={{
                                    opacity: [0, 0.75, 0],
                                    scale: [0.35, 1.25, 1.6],
                                    y: [0, 20, 34],
                                    x: [0, (i - 1) * 7],
                                }}
                                transition={{
                                    duration: 0.9,
                                    repeat: Infinity,
                                    delay: i * 0.22,
                                    ease: "easeOut",
                                }}
                            />
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>

            <motion.button
                type="button"
                className="emmbot-body"
                onClick={goToContact}
                onHoverStart={() => {
                    hoveringRef.current = true;
                }}
                onHoverEnd={() => {
                    hoveringRef.current = false;
                }}
                aria-label="Hablar con EMMPIEZA — ir a contacto"
                title="¿Hablamos?"
                style={
                    phase === "crashed"
                        ? { originX: crashSideRef.current > 0 ? 1 : 0 }
                        : undefined
                }
                animate={
                    reduceMotion
                        ? {}
                        : phase === "crashed"
                            ? {
                                // Se aplasta contra la pared y recupera forma
                                scaleX: [1, 0.45, 1.2, 0.88, 1.05, 1],
                                scaleY: [1, 1.3, 0.82, 1.1, 0.97, 1],
                                rotate: [
                                    crashSideRef.current * 16,
                                    crashSideRef.current * -8,
                                    crashSideRef.current * 4,
                                    0,
                                ],
                                y: 0,
                            }
                            : isMoving
                                ? { rotate: tilt, y: 0 }
                                : { rotate: 0, y: [0, -7, 0] }
                }
                transition={
                    phase === "crashed"
                        ? { duration: 0.9, ease: "easeOut" }
                        : isMoving
                            ? { duration: 0.4 }
                            : { duration: 3.4, repeat: Infinity, ease: "easeInOut" }
                }
                whileHover={{ scale: 1.12 }}
                whileTap={{ scale: 0.92 }}
            >
                <svg viewBox="0 0 64 72" aria-hidden="true">
                    {/* Antena */}
                    <line x1="32" y1="12" x2="32" y2="4" stroke="#8a8a94" strokeWidth="2.5" strokeLinecap="round" />
                    <motion.circle
                        cx="32" cy="4" r="3.5"
                        fill="#c8f04a"
                        animate={reduceMotion ? {} : { opacity: [1, 0.35, 1], scale: [1, 1.25, 1] }}
                        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    />
                    {/* Cabeza (colores propios: visible en tema claro y oscuro) */}
                    <rect x="8" y="12" width="48" height="38" rx="12" fill="#202028" stroke="#494956" strokeWidth="1.5" />
                    {/* Pantalla de la cara */}
                    <rect x="14" y="18" width="36" height="26" rx="8" fill="#0c0c10" />
                    {phase === "crashed" ? (
                        /* Ojos mareados tras el golpe */
                        <g stroke="#c8f04a" strokeWidth="2.4" strokeLinecap="round">
                            <path d="M22 26 L28 32 M28 26 L22 32" />
                            <path d="M36 26 L42 32 M42 26 L36 32" />
                        </g>
                    ) : (
                        <>
                            {/* Ojos (siguen el cursor) */}
                            <motion.g style={{ x: pupilX, y: pupilY }}>
                                <circle cx="25" cy="29" r="4" fill="#c8f04a" />
                                <circle cx="39" cy="29" r="4" fill="#c8f04a" />
                            </motion.g>
                            {/* Párpados (parpadeo) */}
                            <motion.g
                                style={{ originY: "29px" }}
                                animate={reduceMotion ? {} : { scaleY: [0, 0, 1, 0] }}
                                transition={{ duration: 0.36, times: [0, 0.4, 0.7, 1], repeat: Infinity, repeatDelay: 3.8 }}
                            >
                                <rect x="19" y="24" width="12" height="10" rx="4" fill="#0c0c10" />
                                <rect x="33" y="24" width="12" height="10" rx="4" fill="#0c0c10" />
                            </motion.g>
                        </>
                    )}
                    {/* Boca */}
                    {phase === "crashed" ? (
                        <circle cx="32" cy="39" r="2.6" fill="none" stroke="#c8f04a" strokeWidth="2" />
                    ) : (
                        <path d="M27 38 Q32 42 37 38" stroke="#c8f04a" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                    )}
                    {/* Orejas */}
                    <rect x="3" y="26" width="5" height="12" rx="2.5" fill="#8a8a94" />
                    <rect x="56" y="26" width="5" height="12" rx="2.5" fill="#8a8a94" />
                    {/* Cuerpo */}
                    <rect x="18" y="52" width="28" height="16" rx="8" fill="#202028" stroke="#494956" strokeWidth="1.5" />
                    <motion.circle
                        cx="32" cy="60" r="4"
                        fill="#c8f04a"
                        animate={reduceMotion ? {} : { opacity: [0.5, 1, 0.5] }}
                        transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                    />
                </svg>
            </motion.button>
        </motion.div>
    );
}

export default EmmBot;
